/**
 * Status Timeline Component
 * 
 * Displays the dispute lifecycle status history with integrated document management.
 * Auto Starter gets a simplified view, Specialist Pro gets full control.
 * Supports uploading response documents tied to status events.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { metro2Service, DISPUTE_STATUS_INFO, MAIL_TYPES } from '../../services/metro2Service';
import { cachedFetch, fetchCacheKey } from '../../utils/fetchCache';
import { ResponseDocuments, SimpleResponseDocuments } from './ResponseDocuments';
import { formatTimestamp } from '../../utils/dateTime';
import {
  CheckCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Building2,
  Briefcase,
  Mail,
  Search,
  Archive,
  ArrowUp,
  Trash2,
  FileEdit,
  Loader2,
  ChevronDown,
  ChevronUp,
  Plus,
  Calendar,
  Package,
  AlertCircle,
  Paperclip,
  FileText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Icon mapping
const STATUS_ICONS = {
  draft: FileEdit,
  ready_to_send: Send,
  sent_to_bureau: Building2,
  sent_to_furnisher: Briefcase,
  delivered: CheckCircle,
  response_received: Mail,
  reinvestigation_in_progress: Search,
  corrected: CheckCircle2,
  deleted: Trash2,
  verified_remains: XCircle,
  escalated: ArrowUp,
  closed: Archive,
};

// Timeline node component with document support
const TimelineNode = ({ 
  update, 
  isLast, 
  isFirst, 
  simplified, 
  roundId, 
  timelineId,
  linkedDocuments = [],
  onDocumentChange 
}) => {
  const info = DISPUTE_STATUS_INFO[update.status] || { 
    label: update.status, 
    color: 'bg-slate-100 text-slate-700' 
  };
  const Icon = STATUS_ICONS[update.status] || Clock;
  const [expanded, setExpanded] = useState(isFirst);
  
  // Use the shared date formatting utility
  const formatDate = (dateStr) => formatTimestamp(dateStr, { includeSeconds: false });
  
  // Filter documents linked to this status event
  const eventDocuments = linkedDocuments.filter(doc => doc.status_event_id === update.id);
  const isResponseStatus = update.status === 'response_received';
  
  return (
    <div className="flex gap-4" data-testid={`timeline-node-${update.id}`}>
      {/* Vertical line and dot */}
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${info.color}`}>
          <Icon size={18} />
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-slate-200 my-2" />}
      </div>
      
      {/* Content */}
      <div className={`flex-1 pb-6 ${isLast ? '' : ''}`}>
        <div 
          className={`bg-white border rounded-lg p-4 ${simplified ? '' : 'cursor-pointer hover:shadow-sm'}`}
          onClick={() => !simplified && setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-stone-900">{info.label}</span>
              {info.terminal && (
                <Badge className={info.outcome === 'successful' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {info.outcome === 'successful' ? 'Success' : 'Unsuccessful'}
                </Badge>
              )}
              {/* Document indicator */}
              {eventDocuments.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  <Paperclip size={10} className="mr-1" />
                  {eventDocuments.length} doc{eventDocuments.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <span className="text-sm text-slate-500">
              {formatDate(update.status_timestamp)}
            </span>
          </div>
          
          {/* Summary for sent statuses */}
          {(update.destination_name || update.tracking_number) && (
            <div className="mt-2 text-sm text-slate-600">
              {update.destination_name && <span>To: {update.destination_name}</span>}
              {update.tracking_number && <span className="ml-4">Tracking: {update.tracking_number}</span>}
            </div>
          )}
          
          {/* Response summary */}
          {update.response_summary && (
            <div className="mt-2 p-2 bg-slate-50 rounded text-sm text-slate-700">
              {update.response_summary}
            </div>
          )}
          
          {/* Expandable details for Pro mode */}
          {!simplified && expanded && (
            <div className="mt-4 pt-4 border-t space-y-2 text-sm" onClick={(e) => e.stopPropagation()}>
              {update.destination && (
                <div><span className="text-slate-500">Destination:</span> {update.destination}</div>
              )}
              {update.mail_type && (
                <div><span className="text-slate-500">Mail Type:</span> {
                  MAIL_TYPES.find(m => m.value === update.mail_type)?.label || update.mail_type
                }</div>
              )}
              {update.delivery_date && (
                <div><span className="text-slate-500">Delivered:</span> {formatDate(update.delivery_date)}</div>
              )}
              {update.response_date && (
                <div><span className="text-slate-500">Response Date:</span> {formatDate(update.response_date)}</div>
              )}
              {update.notes && (
                <div className="mt-2 p-2 bg-amber-50 rounded">
                  <span className="text-slate-500">Notes:</span> {update.notes}
                </div>
              )}
              
              {/* Linked documents for this status event */}
              {eventDocuments.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-slate-500 text-xs uppercase tracking-wide mb-2 flex items-center gap-1">
                    <FileText size={12} />
                    Attached Documents
                  </p>
                  <div className="space-y-2">
                    {eventDocuments.map(doc => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded"
                      >
                        <div className="flex items-center gap-2">
                          <Paperclip size={14} className="text-slate-400" />
                          <span className="text-sm truncate max-w-[200px]" title={doc.original_filename}>
                            {doc.original_filename}
                          </span>
                        </div>
                        {doc.file_url && (
                          <a 
                            href={doc.file_url.startsWith('file://') 
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/dispute-engine/documents/${doc.id}/download`
                              : doc.file_url
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:text-emerald-700 text-xs"
                          >
                            View
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-xs text-slate-400">
                Updated by: {update.action_source === 'system' ? 'System' : 'User'}
              </div>
            </div>
          )}
          
          {!simplified && (
            <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {expanded ? 'Less details' : 'More details'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Status update form (for Specialist Pro)
const StatusUpdateForm = ({ timelineId, currentStatus, onUpdate }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    destination: '',
    destinationName: '',
    mailType: '',
    trackingNumber: '',
    responseDate: '',
    responseSummary: '',
    notes: '',
  });
  
  const statusOptions = [
    { value: 'ready_to_send', label: 'Ready to Send' },
    { value: 'sent_to_bureau', label: 'Sent to Bureau' },
    { value: 'sent_to_furnisher', label: 'Sent to Furnisher' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'response_received', label: 'Response Received' },
    { value: 'reinvestigation_in_progress', label: 'Reinvestigation In Progress' },
    { value: 'corrected', label: 'Corrected' },
    { value: 'deleted', label: 'Deleted' },
    { value: 'verified_remains', label: 'Verified / Remains' },
    { value: 'escalated', label: 'Escalated' },
    { value: 'closed', label: 'Closed' },
  ];
  
  const bureauOptions = [
    { value: 'bureau', label: 'Credit Bureau' },
    { value: 'furnisher', label: 'Data Furnisher' },
  ];
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.status) {
      toast.error('Please select a status');
      return;
    }
    
    setLoading(true);
    try {
      const result = await metro2Service.addStatusUpdate(
        timelineId,
        {
          ...formData,
          userId: user?.user_id || user?.id,
        },
        token
      );
      
      toast.success(`Status updated to: ${DISPUTE_STATUS_INFO[formData.status]?.label || formData.status}`);
      onUpdate?.(result.timeline);
      
      // Reset form
      setFormData({
        status: '',
        destination: '',
        destinationName: '',
        mailType: '',
        trackingNumber: '',
        responseDate: '',
        responseSummary: '',
        notes: '',
      });
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.message || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };
  
  const showSentFields = ['sent_to_bureau', 'sent_to_furnisher', 'delivered'].includes(formData.status);
  const showResponseFields = ['response_received', 'corrected', 'deleted', 'verified_remains'].includes(formData.status);
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="status-update-form">
      {/* Status */}
      <div className="space-y-2">
        <Label>New Status *</Label>
        <Select 
          value={formData.status} 
          onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}
        >
          <SelectTrigger data-testid="status-select">
            <SelectValue placeholder="Select new status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Sent fields */}
      {showSentFields && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Destination</Label>
              <Select 
                value={formData.destination} 
                onValueChange={(v) => setFormData(p => ({ ...p, destination: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {bureauOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Destination Name</Label>
              <Input
                placeholder="e.g., TransUnion, ABC Collections"
                value={formData.destinationName}
                onChange={(e) => setFormData(p => ({ ...p, destinationName: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Mail Type</Label>
              <Select 
                value={formData.mailType} 
                onValueChange={(v) => setFormData(p => ({ ...p, mailType: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mail type" />
                </SelectTrigger>
                <SelectContent>
                  {MAIL_TYPES.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tracking Number</Label>
              <Input
                placeholder="Enter tracking number"
                value={formData.trackingNumber}
                onChange={(e) => setFormData(p => ({ ...p, trackingNumber: e.target.value }))}
              />
            </div>
          </div>
        </>
      )}
      
      {/* Response fields */}
      {showResponseFields && (
        <>
          <div className="space-y-2">
            <Label>Response Date</Label>
            <Input
              type="date"
              value={formData.responseDate}
              onChange={(e) => setFormData(p => ({ ...p, responseDate: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Response Summary</Label>
            <Textarea
              placeholder="Summarize the response received..."
              value={formData.responseSummary}
              onChange={(e) => setFormData(p => ({ ...p, responseSummary: e.target.value }))}
              rows={3}
            />
          </div>
        </>
      )}
      
      {/* Notes */}
      <div className="space-y-2">
        <Label>Notes (Optional)</Label>
        <Textarea
          placeholder="Add any additional notes..."
          value={formData.notes}
          onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
          rows={2}
        />
      </div>
      
      <Button type="submit" disabled={loading || !formData.status} className="w-full">
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin mr-2" />
            Updating...
          </>
        ) : (
          'Update Status'
        )}
      </Button>
    </form>
  );
};

// Deadline alert component
const DeadlineAlert = ({ deadline, daysRemaining }) => {
  if (!deadline) return null;
  
  const isUrgent = daysRemaining !== null && daysRemaining <= 7;
  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  
  return (
    <div className={`p-4 rounded-lg flex items-center gap-3 ${
      isOverdue ? 'bg-red-50 border border-red-200' :
      isUrgent ? 'bg-amber-50 border border-amber-200' :
      'bg-emerald-50 border border-emerald-200'
    }`}>
      <Calendar size={20} className={
        isOverdue ? 'text-red-600' :
        isUrgent ? 'text-amber-600' :
        'text-emerald-600'
      } />
      <div>
        <p className={`font-medium ${
          isOverdue ? 'text-red-700' :
          isUrgent ? 'text-amber-700' :
          'text-emerald-700'
        }`}>
          {isOverdue ? 'Response Overdue' :
           isUrgent ? 'Response Deadline Approaching' :
           'Response Expected By'}
        </p>
        <p className="text-sm text-slate-600">
          {formatTimestamp(deadline, { includeTime: false })}
          {daysRemaining !== null && (
            <span className="ml-2">
              ({isOverdue ? `${Math.abs(daysRemaining)} days overdue` :
                daysRemaining === 0 ? 'Today' :
                daysRemaining === 1 ? 'Tomorrow' :
                `${daysRemaining} days remaining`})
            </span>
          )}
        </p>
      </div>
    </div>
  );
};

// Main Status Timeline component with integrated document management
export const StatusTimeline = ({ 
  roundId, 
  packageId, 
  timeline: initialTimeline,
  simplified = false,
  onStatusChange,
  lazyLoadDocuments = true,
}) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(!initialTimeline);
  const [timeline, setTimeline] = useState(initialTimeline);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);
  const [documentsExpanded, setDocumentsExpanded] = useState(!lazyLoadDocuments);
  const [documentsError, setDocumentsError] = useState(null);
  const [daysUntilDeadline, setDaysUntilDeadline] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  
  useEffect(() => {
    if (!initialTimeline && roundId) {
      loadTimeline();
    }
  }, [roundId, packageId, initialTimeline]);
  
  const loadDocuments = useCallback(async () => {
    if (!roundId || !token) return;
    
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const cacheKey = fetchCacheKey('round-documents', roundId);
      const result = await cachedFetch(
        cacheKey,
        () => metro2Service.getDocumentsForRound(roundId, token),
        { ttlMs: 30000 }
      );
      setDocuments(result.documents || []);
      setDocumentsLoaded(true);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocumentsError(error?.message || 'Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  }, [roundId, token]);

  useEffect(() => {
    if (!lazyLoadDocuments && roundId) {
      loadDocuments();
    }
  }, [lazyLoadDocuments, roundId, loadDocuments]);

  const handleExpandDocuments = () => {
    setDocumentsExpanded(true);
    if (!documentsLoaded && !documentsLoading) {
      loadDocuments();
    }
  };
  
  const loadTimeline = async () => {
    setLoading(true);
    try {
      let result;
      if (packageId) {
        // TODO: Get package-specific timeline
        result = await metro2Service.getRoundStatusTimeline(roundId, token);
        // Find package timeline
        const pkgTimeline = result.package_timelines?.find(t => t.package_id === packageId);
        setTimeline(pkgTimeline || result.round_timeline);
      } else {
        result = await metro2Service.getRoundStatusTimeline(roundId, token);
        setTimeline(result.round_timeline);
      }
      
      if (result.days_until_deadline !== undefined) {
        setDaysUntilDeadline(result.days_until_deadline);
      }
    } catch (error) {
      console.error('Failed to load timeline:', error);
      // Create timeline if doesn't exist
      try {
        const createResult = await metro2Service.createStatusTimeline(
          roundId, 
          packageId, 
          user?.user_id || user?.id, 
          token
        );
        setTimeline(createResult.timeline);
      } catch (createError) {
        console.error('Failed to create timeline:', createError);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusUpdate = (updatedTimeline) => {
    setTimeline(updatedTimeline);
    setShowUpdateDialog(false);
    onStatusChange?.(updatedTimeline);
    
    // Recalculate days until deadline
    if (updatedTimeline?.response_deadline) {
      const now = new Date();
      const deadline = new Date(updatedTimeline.response_deadline);
      const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
      setDaysUntilDeadline(diff);
    }
  };
  
  const handleDocumentChange = () => {
    loadDocuments();
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="status-timeline-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  
  if (!timeline) {
    return (
      <div className="text-center py-8 text-slate-500" data-testid="status-timeline-empty">
        <Package size={32} className="mx-auto mb-2 opacity-50" />
        <p>No status timeline yet</p>
        {!simplified && (
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-4"
            onClick={loadTimeline}
          >
            Initialize Status Tracking
          </Button>
        )}
      </div>
    );
  }
  
  const statusHistory = timeline.status_history || [];
  const currentStatus = timeline.current_status;
  const currentStatusInfo = DISPUTE_STATUS_INFO[currentStatus] || { 
    label: currentStatus, 
    color: 'bg-slate-100 text-slate-700' 
  };
  
  return (
    <div className="space-y-6" data-testid="status-timeline">
      {/* Current status header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge className={`text-sm px-3 py-1 ${currentStatusInfo.color}`}>
            {currentStatusInfo.label}
          </Badge>
          {currentStatusInfo.terminal && (
            <Badge className={currentStatusInfo.outcome === 'successful' ? 
              'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
              {currentStatusInfo.outcome === 'successful' ? 'Successful' : 'Unsuccessful'}
            </Badge>
          )}
        </div>
        
        {/* Update button for Pro mode */}
        {!simplified && !currentStatusInfo.terminal && (
          <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="update-status-btn">
                <Plus size={16} className="mr-1" />
                Update Status
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Update Dispute Status</DialogTitle>
              </DialogHeader>
              <StatusUpdateForm
                timelineId={timeline.id}
                currentStatus={currentStatus}
                onUpdate={handleStatusUpdate}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
      
      {/* Deadline alert */}
      {timeline.response_deadline && !currentStatusInfo.terminal && (
        <DeadlineAlert 
          deadline={timeline.response_deadline}
          daysRemaining={daysUntilDeadline}
        />
      )}
      
      {/* Outcome summary */}
      {timeline.outcome && (
        <div className={`p-4 rounded-lg ${
          timeline.outcome === 'successful' ? 'bg-emerald-50 border border-emerald-200' :
          timeline.outcome === 'unsuccessful' ? 'bg-red-50 border border-red-200' :
          'bg-slate-50 border border-slate-200'
        }`}>
          <p className={`font-medium ${
            timeline.outcome === 'successful' ? 'text-emerald-700' :
            timeline.outcome === 'unsuccessful' ? 'text-red-700' :
            'text-slate-700'
          }`}>
            Outcome: {timeline.outcome.charAt(0).toUpperCase() + timeline.outcome.slice(1)}
          </p>
          {timeline.outcome_details && (
            <p className="text-sm text-slate-600 mt-1">{timeline.outcome_details}</p>
          )}
        </div>
      )}
      
      {/* Timeline */}
      <div className="mt-4">
        {statusHistory.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Clock size={32} className="mx-auto mb-2 opacity-50" />
            <p>No status updates yet</p>
          </div>
        ) : (
          <div>
            {[...statusHistory].reverse().map((update, index) => (
              <TimelineNode
                key={update.id}
                update={update}
                isFirst={index === 0}
                isLast={index === statusHistory.length - 1}
                simplified={simplified}
                roundId={roundId}
                timelineId={timeline.id}
                linkedDocuments={documents}
                onDocumentChange={handleDocumentChange}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Response Documents Section */}
      <div className="mt-6 pt-6 border-t">
        {lazyLoadDocuments && !documentsExpanded ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-600">Response documents load on demand.</p>
            <Button variant="outline" size="sm" onClick={handleExpandDocuments} data-testid="load-round-documents-btn">
              View response documents
            </Button>
          </div>
        ) : simplified ? (
          <SimpleResponseDocuments 
            roundId={roundId}
            packageId={packageId}
            timelineId={timeline.id}
            deferLoad={lazyLoadDocuments}
            initialDocuments={documents}
            documentsLoading={documentsLoading}
            documentsError={documentsError}
            onRequestLoad={loadDocuments}
          />
        ) : (
          <ResponseDocuments
            roundId={roundId}
            packageId={packageId}
            timelineId={timeline.id}
            documents={documents}
            documentsLoading={documentsLoading}
            documentsError={documentsError}
            deferLoad={lazyLoadDocuments && !documentsLoaded}
            onRequestLoad={loadDocuments}
            onDocumentChange={handleDocumentChange}
          />
        )}
      </div>
    </div>
  );
};

// Simplified timeline for Auto Starter - read-only with document viewing
export const SimpleStatusTimeline = ({ roundId, packageId, timeline }) => {
  return (
    <StatusTimeline
      roundId={roundId}
      packageId={packageId}
      timeline={timeline}
      simplified={true}
    />
  );
};

// Export components
export default StatusTimeline;
